import { useState, useEffect } from 'react'
import { api } from '../../api'
import { useAuth } from '../../AuthContext'
import TelephoneInput from '../../components/auth/TelephoneInput'
import PinDots from '../../components/auth/PinDots'
import { useTheme } from '../../theme'
import { MVP } from '../../mvp'

/**
 * Section « Méthodes de connexion » — page Sécurité du Compte (proto v7.4).
 *
 * ARBITRAGE ENZO (S34) : on ne propose pas encore d'AUTRE methode que le
 * telephone + PIN. Email et Mot de passe sont donc affiches « Bientot » tant
 * qu'ils ne sont pas ouverts — sauf s'ils sont DEJA actifs sur le compte, cas
 * ou l'on montre l'etat reel (on ne cache pas a quelqu'un une methode qu'il
 * possede). Le telephone + PIN, lui, reste pleinement operationnel : c'est la
 * seule porte d'entree, la fermer echouerait les comptes qui n'ont pas encore
 * de PIN.
 *
 * Les modales `LinkEmailModal` et `SetPasswordModal` restent dans ce fichier :
 * elles sont l'implementation prete a servir le jour ou ces methodes s'ouvrent
 * (cf. docs/BACKLOG-COMPTE.md). Elles ne sont simplement plus atteignables.
 *
 * ADR-027 etape 4.
 */
function AuthMethodsSection({ tc, ts, cardBg, inputCls, onChangerPin }) {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const [methods, setMethods] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeModal, setActiveModal] = useState(null) // 'email' | 'phone' | 'password'
  const [toast, setToast] = useState(null)

  const loadMethods = async () => {
    try {
      const data = await api.auth.getAuthMethods()
      setMethods(data)
    } catch {
      setMethods(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (token) loadMethods() }, [token])

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  if (loading || !methods) return null

  // `bientot` : methode non encore proposee (arbitrage Enzo S34). Une methode
  // deja active sur le compte reste affichee comme telle.
  const items = [
    {
      key: 'email',
      icon: '✉️',
      label: 'Email',
      value: methods.email || null,
      active: !!methods.email,
      bientot: !methods.email,
    },
    {
      key: 'phone',
      icon: '📱',
      label: 'Téléphone + PIN',
      value: methods.telephone || null,
      active: methods.hasPin,
      canAdd: !methods.hasPin,
      // Seule methode ouverte : on peut donc changer son code depuis sa ligne.
      action: methods.hasPin && onChangerPin ? { l: 'Modifier', go: onChangerPin } : null,
    },
    {
      key: 'password',
      icon: '🔑',
      label: 'Mot de passe',
      value: null,
      active: methods.hasPassword,
      bientot: !methods.hasPassword,
    },
  ].filter((it) => MVP.AUTH_EMAIL_MDP || !it.bientot) // MVP (S62) : pas de ligne « Bientot »

  return (
    <>
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[200] px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg animate-fade-up ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-600 text-white'
        }`}>{toast.msg}</div>
      )}

      <p className="text-[11px] font-bold uppercase tracking-[0.16em] mx-0.5 mb-2.5 text-teal">
        Méthodes de connexion
      </p>
      <div className={`rounded-[22px] border overflow-hidden mb-3.5 ${cardBg}`}>
        {items.map((item, i) => (
          <div key={item.key}
            className={`flex items-center gap-3 px-4 py-3.5 ${
              i > 0 ? `border-t ${isWeb3 ? 'border-white/5' : 'border-ink/[0.07]'}` : ''
            }`}>
            <span className="w-[34px] h-[34px] rounded-xl flex-shrink-0 flex items-center justify-center text-base"
              style={{ background: 'rgba(20,184,166,0.1)' }}>{item.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${tc}`}>{item.label}</p>
              {item.active && item.value && (
                <p className={`text-[11px] truncate ${ts}`}>{item.value}</p>
              )}
            </div>
            {item.active ? (
              <span className="flex items-center gap-2.5 flex-shrink-0">
                <span className="text-[11.5px] font-bold" style={{ color: '#0a8a5f' }}>Active</span>
                {item.action && (
                  <button onClick={item.action.go}
                    className={`text-xs font-bold rounded-full px-3 py-[6px] ${tc}`}
                    style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.16), rgba(20,184,166,0.12))' }}
                  >{item.action.l}</button>
                )}
              </span>
            ) : item.bientot ? (
              <span className="text-[10.5px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 flex-shrink-0 text-primary"
                style={{ background: 'rgba(244,137,83,0.14)', border: '1px solid rgba(244,137,83,0.3)' }}>
                Bientôt
              </span>
            ) : item.canAdd ? (
              <button
                onClick={() => setActiveModal(item.key)}
                className={`text-xs font-bold rounded-full px-3.5 py-[7px] flex-shrink-0 ${tc}`}
                style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.16), rgba(20,184,166,0.12))' }}
              >+ Ajouter</button>
            ) : null}
          </div>
        ))}
      </div>

      {/* Modals de liaison */}
      {activeModal === 'email' && (
        <LinkEmailModal
 tc={tc} ts={ts} cardBg={cardBg} inputCls={inputCls}
          onClose={() => setActiveModal(null)}
          onSuccess={() => { setActiveModal(null); showToast('Email lié avec succès'); loadMethods() }}
        />
      )}
      {activeModal === 'phone' && (
        <LinkPhoneModal
 tc={tc} ts={ts} cardBg={cardBg} inputCls={inputCls}
          onClose={() => setActiveModal(null)}
          onSuccess={() => { setActiveModal(null); showToast('Téléphone et PIN ajoutés'); loadMethods() }}
        />
      )}
      {activeModal === 'password' && (
        <SetPasswordModal
 tc={tc} ts={ts} cardBg={cardBg} inputCls={inputCls}
          onClose={() => setActiveModal(null)}
          onSuccess={() => { setActiveModal(null); showToast('Mot de passe défini'); loadMethods() }}
        />
      )}
    </>
  )
}

// ── Modal: Ajouter un email ──
function LinkEmailModal({ tc, ts, cardBg, inputCls, onClose, onSuccess }) {
  const { isWeb3 } = useTheme()
  const [step, setStep] = useState('input') // input | otp
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSendOtp = async () => {
    if (!email || !email.includes('@')) { setError('Email invalide'); return }
    setLoading(true)
    setError('')
    try {
      await api.auth.linkEmail(email)
      setStep('otp')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!code || code.length !== 6) { setError('Le code doit contenir 6 chiffres'); return }
    setLoading(true)
    setError('')
    try {
      await api.auth.verifyLinkEmail(email, code)
      onSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl ${isWeb3 ? 'bg-[#0a0a0f] border border-white/10' : 'bg-white'}`}>
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className={`text-base font-bold ${tc}`}>Ajouter un email</h2>
          <button onClick={onClose} className={`p-1.5 rounded-xl bg-ink/5 text-ink/60`}>✕</button>
        </div>
        <div className="px-4 pb-4">
          {step === 'input' ? (
            <>
              <p className={`text-[12px] mb-3 ${ts}`}>Un code de vérification sera envoyé à cette adresse.</p>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError('') }}
                placeholder="votre@email.com" className={`${inputCls} mb-2`} disabled={loading} />
            </>
          ) : (
            <>
              <p className={`text-[12px] mb-3 ${ts}`}>Code envoyé à <strong>{email}</strong></p>
              <input type="text" inputMode="numeric" maxLength={6} value={code}
                onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError('') }}
                placeholder="000000" className={`${inputCls} text-center text-lg font-mono tracking-[0.3em] mb-2`} disabled={loading} />
            </>
          )}
          {error && <p className="text-[12px] text-red-500 mb-2">{error}</p>}
          <button
            onClick={step === 'input' ? handleSendOtp : handleVerify}
            disabled={loading}
            className="w-full py-3 rounded-xl text-white text-[13px] font-bold disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}
          >
            {loading ? 'Chargement...' : step === 'input' ? 'Envoyer le code' : 'Vérifier'}
          </button>
          {step === 'otp' && (
            <button onClick={() => { setStep('input'); setCode(''); setError('') }}
              className={`w-full mt-2 py-2 text-[12px] font-medium ${ts}`}>
              Modifier l'email
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Modal: Ajouter telephone + PIN ──
function LinkPhoneModal({ tc, ts, cardBg, inputCls, onClose, onSuccess }) {
  const { isWeb3 } = useTheme()
  const tChamps = {
    inputBg: isWeb3 ? 'bg-[#0a0e13]' : 'bg-creme',
    inputBorder: isWeb3 ? 'border-[#252d38]' : 'border-[#ede4db]',
    inputText: isWeb3 ? 'text-white placeholder-white/30' : 'text-ink placeholder-ink/60',
    textSecondary: ts,
    textMuted: ts,
  }
  const [telephone, setTelephone] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!telephone) { setError('Le téléphone est obligatoire'); return }
    if (!pin || pin.length < 4 || pin.length > 6) { setError('Le PIN doit contenir 4 à 6 chiffres'); return }
    if (pin !== pinConfirm) { setError('Les PIN ne correspondent pas'); return }
    setLoading(true)
    setError('')
    try {
      await api.auth.linkPhonePin(telephone, pin)
      onSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl ${isWeb3 ? 'bg-[#0a0a0f] border border-white/10' : 'bg-white'}`}>
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className={`text-base font-bold ${tc}`}>Ajouter téléphone + PIN</h2>
          <button onClick={onClose} className={`p-1.5 rounded-xl bg-ink/5 text-ink/60`}>✕</button>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <TelephoneInput id="link-tel" label="Téléphone" value={telephone}
            onChange={(v) => { setTelephone(v); setError('') }} t={tChamps} disabled={loading} />
          <div>
            {/* Le backend exige EXACTEMENT 6 chiffres (LinkPhonePinDto) — l'intitule
                disait « 4-6 », ce qui promettait un PIN que l'API refuse. */}
            <p className={`text-[11px] font-medium ${ts} mb-1`}>Code PIN (6 chiffres)</p>
            <PinDots id="link-pin" value={pin} onChange={(v) => { setPin(v); setError('') }}
              t={tChamps} disabled={loading} />
          </div>
          <div>
            <p className={`text-[11px] font-medium ${ts} mb-1`}>Confirmer le PIN</p>
            <PinDots id="link-pin-confirm" value={pinConfirm} onChange={(v) => { setPinConfirm(v); setError('') }}
              t={tChamps} disabled={loading}
              aide={pinConfirm.length === 6 && pinConfirm !== pin ? '⚠️ Les deux codes ne correspondent pas' : 'Saisissez-le une seconde fois'} />
          </div>
          {error && <p className="text-[12px] text-red-500">{error}</p>}
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-3 rounded-xl text-white text-[13px] font-bold disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}>
            {loading ? 'Chargement...' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal: Definir un mot de passe ──
function SetPasswordModal({ tc, ts, cardBg, inputCls, onClose, onSuccess }) {
  const { isWeb3 } = useTheme()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!password || password.length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères'); return }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return }
    setLoading(true)
    setError('')
    try {
      await api.auth.setPassword(password)
      onSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl ${isWeb3 ? 'bg-[#0a0a0f] border border-white/10' : 'bg-white'}`}>
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className={`text-base font-bold ${tc}`}>Définir un mot de passe</h2>
          <button onClick={onClose} className={`p-1.5 rounded-xl bg-ink/5 text-ink/60`}>✕</button>
        </div>
        <div className="px-4 pb-4 space-y-3">
          <p className={`text-[12px] ${ts}`}>Vous pourrez ensuite vous connecter avec email + mot de passe.</p>
          <div>
            <label className={`text-[11px] font-medium ${ts} block mb-1`}>Mot de passe</label>
            <input type="password" value={password} onChange={e => { setPassword(e.target.value); setError('') }}
              placeholder="6 caractères minimum" className={inputCls} disabled={loading} />
          </div>
          <div>
            <label className={`text-[11px] font-medium ${ts} block mb-1`}>Confirmer</label>
            <input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setError('') }}
              placeholder="Retapez le mot de passe" className={inputCls} disabled={loading} />
          </div>
          {error && <p className="text-[12px] text-red-500">{error}</p>}
          <button onClick={handleSubmit} disabled={loading}
            className="w-full py-3 rounded-xl text-white text-[13px] font-bold disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), rgb(var(--c-primary-soft)))' }}>
            {loading ? 'Chargement...' : 'Définir le mot de passe'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AuthMethodsSection
